import _ from 'lodash';
import { api } from 'careand-redux/api/ApiThunks';
import moment from 'moment-timezone';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { RevenuePlot, RevenueTable } from 'components/Clinic/ClinicFinancials/Revenue';
import React, { useEffect, useState, useMemo } from 'react';
import { type Filter } from '../..';
import './style.scss';
//TODO remove and move styles elsewhere
import 'components/Clinic/ClinicFinancials/style.scss';
import 'components/Clinic/ClinicStats/style.scss';
import InlineToggle from '../../components/InlineToggle';
import { ReportingData } from 'components/Clinic/ClinicFinancials/Reporting';
import {
  Subscriptions as OriginalSubscriptions,
  Unsubscriptions as OriginalUnsubscriptions,
} from 'components/Clinic/ClinicFinancials/Subscriptions';
import RetentionGraph from './Graphs/retention';
import useWebWorker from 'workers/useWebWorker';
//@ts-ignore
import ReferralCodesWorker from 'workers/referral-codes.worker?worker';
import ATable from 'components/Clinic/ClinicFinancials/ATable';
import { useAppSelector } from 'careand-redux/store';
import PatientUtils from 'services/PatientUtils';
import { APlot } from 'components/Clinic/ClinicFinancials/APlot';
import useFetchPatients from '../../../../../hooks/useFetchPatients';
import {
  FinancialStatistics,
  Invoice,
  StatisticsEvent,
  Subscription,
} from '../../../../../generated/api';
import { useStatistics } from '../../context';
import {
  getConversionSummary,
  getNewHealthConcernIds,
  isPPUConversionAppointment,
} from 'services/PPUConversionUtils';

type RangeT = {
  name: string;
  fullName: string;
  ranges: readonly { start: number; end: number }[];
  unitFormat: string;
};

export const RANGES = [
  {
    name: 'All',
    fullName: 'All Time',
    ranges: [
      {
        start: moment('2021-01-01', 'YYYY-MM-DD').startOf('month').unix(),
        end: moment().unix(),
      },
    ],
    unitFormat: 'YYYY-MM',
  },
  {
    name: '1W',
    fullName: '1 Week',
    ranges: [
      { start: moment().add(-1, 'weeks').unix(), end: moment().unix() },
      {
        start: moment().add(-2, 'weeks').unix(),
        end: moment().add(-1, 'weeks').unix(),
      },
    ],
    unitFormat: 'YYYY-MM-DD',
  },
  {
    name: '4W',
    fullName: '4 Weeks',
    ranges: [
      { start: moment().add(-4, 'weeks').unix(), end: moment().unix() },
      {
        start: moment().add(-8, 'weeks').unix(),
        end: moment().add(-4, 'weeks').unix(),
      },
    ],
    unitFormat: 'YYYY-MM-DD',
  },
  {
    name: '1Y',
    fullName: '1 Year',
    ranges: [
      { start: moment().add(-1, 'year').unix(), end: moment().unix() },
      {
        start: moment().add(-2, 'year').unix(),
        end: moment().add(-1, 'year').unix(),
      },
    ],
    unitFormat: 'YYYY-MM',
  },
  {
    name: 'MTD',
    fullName: 'Month to Date',
    ranges: [
      { start: moment().startOf('month').unix(), end: moment().unix() },
      {
        start: moment().add(-1, 'month').startOf('month').unix(),
        end: moment().add(-1, 'month').unix(),
      },
    ],
    unitFormat: 'YYYY-MM-DD',
  },
  {
    name: 'QTD',
    fullName: 'Quarter to Date',
    ranges: [
      {
        start: moment().startOf('quarter').unix(),
        end: moment().unix(),
      },
      {
        start: moment().add(-1, 'quarter').startOf('quarter').unix(),
        end: moment().add(-1, 'quarter').unix(),
      },
    ],
    unitFormat: 'YYYY-MM-DD',
  },
  {
    name: 'YTD',
    fullName: 'Year to Date',
    ranges: [
      { start: moment().startOf('year').unix(), end: moment().unix() },
      {
        start: moment().add(-1, 'year').startOf('year').unix(),
        end: moment().add(-1, 'year').unix(),
      },
    ],
    unitFormat: 'YYYY-MM',
  },
] as const;

export type Range = (typeof RANGES)[number]['name'];

const FinancialsSection = ({ filter }: { filter: Filter }) => {
  const dispatch = useAppDispatch();
  const [data, setData] = useState<FinancialStatistics | undefined>();

  const range: RangeT = useMemo(() => {
    return RANGES.find((it) => it.name === filter.financialsRange) ?? RANGES[0];
  }, [filter.financialsRange]);

  const clinicId = useMemo(() => {
    return filter.type === 'clinic' ? filter.id : undefined;
  }, [filter.type, filter.id]);

  useEffect(() => {
    dispatch(api.Stats.getFinancialStats()).then(setData);
  }, [dispatch]);

  const invoices = useMemo(() => {
    if (clinicId) {
      return data?.invoices!.filter((invoice) => invoice.clinic_id === clinicId);
    }
    return data?.invoices;
  }, [data?.invoices, clinicId]);

  const conversionData = usePPUConversionData(clinicId);

  return (
    <div className="financials-section">
      <Revenue invoices={invoices} range={range} clinicId={clinicId} />
      <Reporting
        invoices={invoices}
        subscriptions={data?.subscriptions}
        range={range}
        conversionData={conversionData}
      />
      <Subscriptions
        subscriptions={data?.subscriptions}
        overdueSubscriptions={data?.overdueSubscriptions}
      />
      <Unsubscriptions
        subscriptions={data?.subscriptions}
        events={data?.events}
        overdueSubscriptions={data?.overdueSubscriptions}
      />
      <Retention subscriptions={data?.subscriptions} />
      <ReferralCodes referralCodesUsed={data?.referralCodesUsed ?? []} />
    </div>
  );
};

function Retention({ subscriptions }: { subscriptions?: Subscription[] }) {
  const showHistoricalRetentionData = useState(false);

  return (
    <div className="dashboard-box overflow-hidden">
      <span className="dashboard-section-title">
        Subscriber Retention
        <InlineToggle
          titleOn="Historical Data"
          titleOff="Recent Data"
          handler={showHistoricalRetentionData}
        />
      </span>
      {subscriptions && (
        <RetentionGraph
          subscriptions={subscriptions}
          showHistoricalData={showHistoricalRetentionData[0]}
        />
      )}
    </div>
  );
}

function Revenue({
  invoices,
  range,
  clinicId,
}: {
  invoices: Invoice[] | undefined;
  range: RangeT | undefined;
  clinicId: string | undefined;
}) {
  const showNet = useState(false);

  return (
    <div className="financials-revenue-section grid gap-[var(--inner-gap)]">
      <div className="dashboard-box">
        <span className="dashboard-section-title">
          Revenue
          <InlineToggle titleOn="NET" titleOff="GROSS" handler={showNet} />
        </span>
        <div className="auto-two-cols">
          <RevenuePlot invoices={invoices} net={showNet[0]} range={range} showTitle={false} />
          <RevenueTable invoices={invoices} net={showNet[0]} />
        </div>
      </div>
    </div>
  );
}

/**
 * PPU -> subscription conversion is measured off appointments, not invoices:
 * a paid invoice can't be traced back to the concern that produced it, and
 * only a New Health Concern visit counts. Sourcing it from the same clinic
 * statistics the "PPU Conversion Rates" chart uses keeps the two in step.
 */
function usePPUConversionData(clinicId?: string) {
  const { data } = useStatistics();
  const physicians = useAppSelector((state) => state.physicians.byId);
  const options = useAppSelector((state) => state.appointments.options);

  return useMemo(() => {
    const newConcernIds = getNewHealthConcernIds(options);
    const appointments = (data?.appointments ?? []).filter((appointment) =>
      isPPUConversionAppointment(appointment, newConcernIds, physicians, clinicId),
    );
    const { months, thisYear, lastMonth } = getConversionSummary(appointments, data?.subscriptions);

    return {
      months,
      thisYear: thisYear === null ? undefined : thisYear.toFixed(2),
      lastMonth: lastMonth === null ? undefined : lastMonth.toFixed(2),
    };
  }, [data?.appointments, data?.subscriptions, physicians, options, clinicId]);
}

function Reporting({
  invoices,
  subscriptions,
  range,
  conversionData,
}: {
  invoices: Invoice[] | undefined;
  subscriptions: Subscription[] | undefined;
  range: RangeT | undefined;
  conversionData: ReturnType<typeof usePPUConversionData>;
}) {
  return (
    <div className="financials-reporting-section">
      <div className="dashboard-box">
        <span className="dashboard-section-title">Reporting</span>
        <ReportingData
          invoices={invoices}
          subscriptions={subscriptions}
          range={range}
          conversionData={conversionData}
        />
      </div>
    </div>
  );
}

function Subscriptions({
  subscriptions,
  overdueSubscriptions,
}: {
  subscriptions: Subscription[] | undefined;
  overdueSubscriptions: Subscription[] | undefined;
}) {
  return (
    <div className="financials-subscriptions-section">
      <div className="dashboard-box">
        <span className="dashboard-section-title">Subscriptions</span>
        <OriginalSubscriptions
          subscriptions={subscriptions}
          //@ts-expect-error
          overdueSubscriptions={overdueSubscriptions}
        />
      </div>
    </div>
  );
}

function Unsubscriptions({
  subscriptions,
  events,
  overdueSubscriptions,
}: {
  subscriptions: Subscription[] | undefined;
  events: StatisticsEvent[] | undefined;
  overdueSubscriptions: Subscription[] | undefined;
}) {
  return (
    <div className="financials-unsubscriptions-section">
      <div className="dashboard-box">
        <span className="dashboard-section-title">Unsubscriptions</span>
        <OriginalUnsubscriptions
          subscriptions={subscriptions}
          events={events}
          overdueSubscriptions={overdueSubscriptions}
        />
      </div>
    </div>
  );
}

function ReferralCodes({ referralCodesUsed }: { referralCodesUsed: StatisticsEvent[] }) {
  return (
    <div className="dashboard-box">
      <span className="dashboard-section-title">Referral Program Leaderboard</span>
      <div className="auto-two-cols">
        <ReferralCodesUsedByMonth referralCodesUsed={referralCodesUsed} />
        <ReferralCodesLeaderboard referralCodesUsed={referralCodesUsed} />
      </div>
    </div>
  );
}

function ReferralCodesLeaderboard({ referralCodesUsed }: { referralCodesUsed: StatisticsEvent[] }) {
  const dispatch = useAppDispatch();
  const headers = ['Patient', '#referrals'];
  const patients = useAppSelector((state) => state.patients.byId);
  const [loadingPatients, setLoadingPatients] = useState<boolean>(false);
  const [data, setData] = useState<
    | {
        missingPatients: string[];
        entries: [string, number][];
      }
    | undefined
  >();

  const message = useMemo(() => {
    return {
      patients: new Set(Object.keys(patients)),
      events: referralCodesUsed,
    };
  }, [patients, referralCodesUsed]);

  useWebWorker(ReferralCodesWorker, message, setData);

  useEffect(() => {
    if (!data || data.missingPatients.length === 0 || loadingPatients) {
      return;
    }
    setLoadingPatients(true);
    dispatch(
      api.Patients.getMultiplePatients({
        multipleValuesPayload: { values: data.missingPatients },
      }),
    ).finally(() => setLoadingPatients(false));
  }, [data, loadingPatients]);

  const rows = useMemo(() => {
    if (!data) return [];

    return _(data.entries)
      .map(([patient_id, count]) => {
        const name = PatientUtils.getName(patients[patient_id], patient_id || 'N/A', false, false);
        const key = <PatientLink name={name!} patient_id={patient_id} />;
        return [key, count];
      })
      .value();
  }, [data, patients]);

  return <ATable headers={headers} rows={rows} />;
}

const PatientLink = ({ name, patient_id }: { name: string; patient_id: string }) => {
  return (
    <a href={`/patient/${patient_id}`} className="text-black hover:text-[#2693d1]">
      {name}
    </a>
  );
};

const ReferralCodesUsedByMonth = ({
  referralCodesUsed,
}: {
  referralCodesUsed: StatisticsEvent[];
}) => {
  const datasets = [
    {
      label: 'Referral Codes Used',
      data: _(referralCodesUsed)
        .groupBy((it) => moment.unix(it.date!).format('YYYY-MM'))
        .entries()
        .map(([date, evs]) => ({
          x: date,
          y: evs.length,
        }))
        .orderBy((it) => it.x, 'asc')
        .value(),
    },
  ];
  //@ts-expect-error
  return <APlot datasets={datasets} forceHeight />;
};

export default FinancialsSection;
